import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { translateClientValidationError } from './utils/client-validation-error';
import { ErrorResponse } from './models/error-response-model';
import { toast } from 'react-toastify';
import { CustomToastContent } from './components/CustomToastContent';
import { toastOptionsTopRight } from './utils/Enums/ToastOptions';
import { clearStoredSession, isImpersonatedToken } from './utils/auth-storage';

const instance = Object.assign(
    axios.create({
        baseURL: `${process.env.REACT_APP_API_URL}`,
    }),
    {}
);

let refreshPromise: Promise<string> | null = null;

const logoutAndRedirect = () => {
    clearStoredSession();

    if (!window.location.pathname.endsWith('/login')) {
        window.location.href = '/login';
    }
};

const isAuthEndpoint = (config: AxiosRequestConfig, endpoint: 'login' | 'refresh') => {
    const url = config.url || '';
    const path = (() => {
        try {
            return new URL(url, config.baseURL || process.env.REACT_APP_API_URL).pathname;
        } catch {
            return url;
        }
    })();

    return path.endsWith(`/${endpoint}`) || path === endpoint;
};

const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
        throw new Error('Missing refresh token');
    }

    const response = await axios.post(`${process.env.REACT_APP_API_URL}refresh`, undefined, {
        headers: {
            'Refresh-Token': refreshToken,
        },
    });
    const newAccessToken = response.headers['authorization'];
    const newRefreshToken = response.headers['refresh-token'];

    if (!newAccessToken || !newRefreshToken) {
        throw new Error('Refresh response is missing token headers');
    }

    localStorage.setItem('token', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken);

    return newAccessToken;
};

const getRefreshPromise = () => {
    if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
};

instance.interceptors.response.use(
    (response) => response.data,
    async (error: AxiosError) => {
        if (error.config?.skipErrorInterceptor) {
            return Promise.reject(error.response?.data);
        }
        const status = error.response?.status;
        if (error.config?.showOwnError && error.response && status !== 401 && status !== 403) {
            return Promise.reject(error.response.data);
        }

        if (status && status >= 500 && status < 600) {
            console.log('Server Connection Error:', error.response?.data);
            toast.error(
                <CustomToastContent content={['Something went wrong with the server.']} />,
                toastOptionsTopRight
            );
            return Promise.reject(error.response?.data);
        }

        switch (status) {
            case 401: {
                const originalRequest = error.config;

                if (
                    !originalRequest ||
                    originalRequest._retry ||
                    originalRequest.skipAuthRefresh ||
                    isAuthEndpoint(originalRequest, 'login') ||
                    isAuthEndpoint(originalRequest, 'refresh')
                ) {
                    logoutAndRedirect();
                    return Promise.reject(error.response?.data);
                }

                originalRequest._retry = true;

                try {
                    const newAccessToken = await getRefreshPromise();
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

                    return instance(originalRequest);
                } catch (refreshError) {
                    logoutAndRedirect();
                    return Promise.reject(error.response?.data);
                }
            }
            case 403: {
                const method = error.config?.method?.toLowerCase();
                const isReadRequest = !method || ['get', 'head', 'options'].includes(method);
                const message = isImpersonatedToken(localStorage.getItem('token'))
                    ? 'impersonationReadOnly'
                    : 'forbidden';
                toast.error(<CustomToastContent content={[message]} translated />, toastOptionsTopRight);

                if (isReadRequest) {
                    return error.response?.data ?? {};
                }

                return Promise.reject(error.response?.data ?? error);
            }
            case 400: {
                const clientMessage = translateClientValidationError(error.response?.data);
                if (clientMessage) {
                    toast.error(<CustomToastContent content={[clientMessage]} />, toastOptionsTopRight);
                    return Promise.reject(error.response?.data);
                }
                if (error.response?.data) {
                    let errorResponse: ErrorResponse = error.response.data;
                    if (errorResponse.type === 'VALIDATION' && errorResponse.validations.length > 0) {
                        let errors = errorResponse.validations.map((value) => `${value.name} ${value.error}`);
                        if (errors.length === 1) {
                            toast.error(<CustomToastContent translated content={[...errors]} />, toastOptionsTopRight);
                        } else {
                            toast.error(
                                <CustomToastContent translated content={[errorResponse.errorCode, ...errors]} />,
                                toastOptionsTopRight
                            );
                        }
                    } else {
                        if (+errorResponse.errorCode === 0) break;
                        else if (+errorResponse.errorCode > 0 && +errorResponse.errorCode < 9)
                            toast.error(
                                <CustomToastContent translated content={[errorResponse.errorCode]} />,
                                toastOptionsTopRight
                            );
                        else if (error.response?.headers['error']) {
                            toast.error(
                                <CustomToastContent translated content={[error.response.headers['error']]}/>,
                                toastOptionsTopRight
                            );
                        }
                        else
                            toast.error(<CustomToastContent translated content={['apiError']} />, toastOptionsTopRight);
                    }

                    console.error('Backend Server returned an error:', errorResponse);
                }
                break;
            }
            case 404: {
                toast.error(<CustomToastContent content={['serviceNotFound']} translated />, toastOptionsTopRight);
                break;
            }
            case 409: {
                if (error.response?.data) {
                    let errorResponse: ErrorResponse = error.response.data;

                    if (errorResponse.type === 'VALIDATION') {
                        let errors = errorResponse.validations.map((value) => `${value.name}: ${value.error}`);

                        toast.error(<CustomToastContent content={errors} />, toastOptionsTopRight);

                        /*`${value.name} ${value.error}`);
                        toast(<CustomToastContent content={errors} translated />, toastOptionsTopRight);*/
                    }
                    console.error('Backend Server returned an error:', errorResponse);
                }
                break;
            }
            default: {

                console.log('Unmapped error', error.response?.data);
                return Promise.reject(error.response?.data);
            }
        }
        return Promise.reject(error.response?.data);
    }
);

// Where you would set stuff like your 'Authorization' header, etc ...
// instance.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
instance.defaults.headers.post['Content-Type'] = 'aplication/json';
instance.defaults.headers.post['user'] = '1';

// Also add/ configure interceptors && all the other cool stuff

// instance.interceptors.request...
instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

export default instance;
